import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CircularProgress from "@mui/material/CircularProgress";

interface OcrFeedbackButtonsProps {
  /**
   * Callback when thumbs up is clicked
   */
  onThumbsUp: () => void;
  /**
   * Callback when thumbs down is clicked
   */
  onThumbsDown: () => void;
  /**
   * Current feedback rating ('up' | 'down' | null)
   */
  currentRating?: "up" | "down" | null;
  /**
   * Whether the buttons are disabled
   */
  disabled?: boolean;
  /**
   * Whether feedback is being submitted
   */
  loading?: boolean;
  /**
   * Size of the buttons
   */
  size?: "small" | "medium" | "large";
}

/**
 * Displays thumbs up/down buttons for OCR result feedback
 * Highlights the selected rating and allows users to change their feedback
 */
function OcrFeedbackButtons({
  onThumbsUp,
  onThumbsDown,
  currentRating = null,
  disabled = false,
  loading = false,
  size = "medium",
}: OcrFeedbackButtonsProps) {
  const handleThumbsUp = () => {
    if (!disabled && !loading) {
      onThumbsUp();
    }
  };

  const handleThumbsDown = () => {
    if (!disabled && !loading) {
      onThumbsDown();
    }
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="Approve OCR results">
        <span>
          <IconButton
            onClick={handleThumbsUp}
            disabled={disabled || loading}
            size={size}
            color={currentRating === "up" ? "success" : "default"}
            sx={{
              "&:hover": {
                backgroundColor:
                  currentRating === "up"
                    ? "success.light"
                    : "action.hover",
              },
            }}
          >
            {loading && currentRating === "up" ? (
              <CircularProgress size={20} />
            ) : (
              <ThumbUpIcon
                sx={{
                  color:
                    currentRating === "up"
                      ? "success.main"
                      : "action.active",
                }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Reject OCR results">
        <span>
          <IconButton
            onClick={handleThumbsDown}
            disabled={disabled || loading}
            size={size}
            color={currentRating === "down" ? "error" : "default"}
            sx={{
              "&:hover": {
                backgroundColor:
                  currentRating === "down"
                    ? "error.light"
                    : "action.hover",
              },
            }}
          >
            {loading && currentRating === "down" ? (
              <CircularProgress size={20} />
            ) : (
              <ThumbDownIcon
                sx={{
                  color:
                    currentRating === "down"
                      ? "error.main"
                      : "action.active",
                }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default OcrFeedbackButtons;
